import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiPlusBold,
    PiPencilSimpleBold,
    PiTrashBold,
    PiHandshakeBold,
    PiCalendarBold,
    PiTargetBold,
    PiPercentBold,
    PiCurrencyCircleDollarBold,
    PiBarbellBold,
    PiCheckCircleBold,
} from 'react-icons/pi';
import { Button, PageHeader, LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import type { BucklerOnePartnership, FranchisorOption } from './types';

function fmtBrl(value: number) {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Máscara BRL para inputs (inteiro, sem centavos). */
function formatBrlInput(value: number): string {
    if (!value) return '';
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
    }).format(value);
}

function parseBrlInput(raw: string): number {
    const digits = raw.replace(/\D/g, '');
    if (!digits) return 0;
    return Number(digits);
}

function fmtPct(value: number) {
    return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
}

function fmtDate(iso: string) {
    return new Date(iso + 'T12:00:00').toLocaleDateString('pt-BR');
}

type FormData = {
    client_id: number | null;
    contract_started_at: string;
    target_total_brl: number;
    min_buckler_unit_pct: number;
    cashback_rate: number;
    reference_machine_value_brl: number | null;
    conditions_notes: string | null;
    cashback_notes: string | null;
    min_cashback_paid: boolean;
    min_cashback_paid_amount_brl: number | null;
    min_cashback_paid_at: string | null;
    active: boolean;
};

function ProgressBar({ value, max = 100, color = 'orange' }: { value: number; max?: number; color?: string }) {
    const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
    const colorClass = color === 'green' ? 'bg-green-500' : color === 'red' ? 'bg-red-500' : 'bg-orange-500';
    return (
        <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-zinc-700 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${colorClass}`} style={{ width: `${pct}%` }} />
        </div>
    );
}

function franchisorInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function FranchisorLogo({
    name,
    logoUrl,
    size = 'lg',
}: {
    name: string;
    logoUrl: string | null | undefined;
    size?: 'md' | 'lg';
}) {
    const [failed, setFailed] = useState(false);
    const dim = size === 'lg' ? 'size-[4.5rem]' : 'size-14';
    const showImage = logoUrl && !failed;

    return (
        <div
            className={`${dim} shrink-0 overflow-hidden rounded-2xl border border-gray-200/80 bg-gradient-to-br from-gray-50 to-white p-2.5 shadow-sm ring-1 ring-black/[0.04] dark:border-zinc-700 dark:from-zinc-800 dark:to-zinc-900 dark:ring-white/10`}
        >
            {showImage ? (
                <img
                    src={logoUrl}
                    alt={name}
                    className="size-full object-contain object-center"
                    onError={() => setFailed(true)}
                />
            ) : (
                <span
                    className="flex size-full items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-lg font-black text-white"
                    aria-hidden
                >
                    {franchisorInitials(name)}
                </span>
            )}
        </div>
    );
}

function PartnershipCard({
    partnership,
    onEdit,
    onDelete,
}: {
    partnership: BucklerOnePartnership;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const { franchisor, progress } = partnership;
    const displayName = franchisor?.name ?? 'Franqueadora';
    const subtitle = franchisor?.legal_name?.trim();
    const showSubtitle = subtitle && subtitle.toLocaleLowerCase() !== displayName.toLocaleLowerCase();
    const bucklerColor = progress.meets_min_buckler_pct ? 'green' : 'red';

    return (
        <article className="rounded-2xl border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden shadow-sm">
            <div className="h-1 w-full bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-300" />
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 dark:border-zinc-800 px-5 py-4">
                <div className="flex items-start gap-4 min-w-0">
                    <FranchisorLogo name={displayName} logoUrl={franchisor?.logo_url} size="lg" />
                    <div className="min-w-0 pt-0.5">
                        <h3 className="font-black text-gray-900 dark:text-white leading-tight truncate">
                            {displayName}
                        </h3>
                        {showSubtitle && (
                            <p className="text-xs text-gray-500 dark:text-zinc-400 truncate mt-0.5">{subtitle}</p>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            <span className="inline-flex items-center gap-1">
                                <PiCalendarBold className="w-3.5 h-3.5" />
                                Contrato desde {fmtDate(partnership.contract_started_at)}
                            </span>
                            <span className="text-gray-300">·</span>
                            <span>{progress.network_units_count} unidade(s)</span>
                            {!partnership.active && (
                                <span className="rounded bg-gray-200 px-1.5 py-0.5 text-gray-600">Inativa</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex shrink-0 gap-1">
                    <button type="button" onClick={onEdit} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-orange-600">
                        <PiPencilSimpleBold className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={onDelete} className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600">
                        <PiTrashBold className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="grid gap-4 p-5 sm:grid-cols-2">
                {/* Meta total */}
                <div className="rounded-xl border border-orange-100 bg-orange-50/60 p-4 dark:border-orange-900/40 dark:bg-orange-950/20">
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-orange-700 dark:text-orange-400">
                        <PiTargetBold className="w-4 h-4" />
                        Meta de vendas
                    </div>
                    <p className="text-lg font-black text-gray-900 dark:text-white">
                        {fmtBrl(progress.total_sales_brl)}
                        <span className="text-sm font-medium text-gray-500"> / {fmtBrl(partnership.target_total_brl)}</span>
                    </p>
                    {progress.progress_pct !== null && (
                        <>
                            <ProgressBar value={progress.progress_pct} />
                            <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-400">
                                {fmtPct(progress.progress_pct)} da meta · faltam {fmtBrl(progress.remaining_to_target_brl)}
                            </p>
                        </>
                    )}
                    {progress.machines_to_target !== null && progress.machines_to_target > 0 && (
                        <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-orange-800 dark:text-orange-300">
                            <PiBarbellBold className="w-3.5 h-3.5" />
                            ~{progress.machines_to_target} máquina(s) para atingir a meta
                            {partnership.reference_machine_value_brl && (
                                <span className="font-normal text-gray-500">
                                    (ticket ref. {fmtBrl(partnership.reference_machine_value_brl)})
                                </span>
                            )}
                        </p>
                    )}
                </div>

                {/* % Buckler mínimo */}
                <div className={`rounded-xl border p-4 ${
                    progress.meets_min_buckler_pct
                        ? 'border-green-100 bg-green-50/60 dark:border-green-900/40 dark:bg-green-950/20'
                        : 'border-red-100 bg-red-50/60 dark:border-red-900/40 dark:bg-red-950/20'
                }`}>
                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                        <PiPercentBold className="w-4 h-4" />
                        Mix Buckler (mín. {fmtPct(partnership.min_buckler_unit_pct)})
                    </div>
                    <p className="text-lg font-black text-gray-900 dark:text-white">
                        {fmtPct(progress.buckler_pct_by_value)}
                        <span className="text-sm font-medium text-gray-500"> do faturamento</span>
                    </p>
                    <ProgressBar value={progress.buckler_pct_by_value} max={partnership.min_buckler_unit_pct} color={bucklerColor} />
                    <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-400">
                        {progress.buckler_deals} de {progress.total_deals} deals · {fmtBrl(progress.buckler_sales_brl)} Buckler One
                    </p>
                    {progress.machines_for_min_pct !== null && progress.machines_for_min_pct > 0 && (
                        <p className="mt-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                            ~{progress.machines_for_min_pct} máquina(s) Buckler para cumprir o mínimo de {fmtPct(partnership.min_buckler_unit_pct)}
                        </p>
                    )}
                </div>

                {/* Cashback */}
                <div className={`rounded-xl border p-4 sm:col-span-2 ${
                    partnership.min_cashback_paid
                        ? 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/40 dark:bg-emerald-950/25'
                        : 'border-blue-100 bg-blue-50/60 dark:border-blue-900/40 dark:bg-blue-950/20'
                }`}>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-400">
                            <PiCurrencyCircleDollarBold className="w-4 h-4" />
                            Cashback franqueadora — {fmtPct(partnership.cashback_rate * 100)}
                        </div>
                        {partnership.min_cashback_paid ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-100 px-2.5 py-0.5 text-xs font-black uppercase tracking-wide text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                <PiCheckCircleBold className="w-3.5 h-3.5" />
                                Pago
                            </span>
                        ) : (
                            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-black uppercase tracking-wide text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
                                Pendente
                            </span>
                        )}
                    </div>

                    {partnership.min_cashback_paid ? (
                        <>
                            <p className="text-xl font-black text-emerald-900 dark:text-emerald-100">
                                {fmtBrl(partnership.min_cashback_paid_amount_brl ?? 0)}
                                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400"> valor pago</span>
                            </p>
                            {partnership.min_cashback_paid_at && (
                                <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-400">
                                    Pago em {fmtDate(partnership.min_cashback_paid_at)}
                                </p>
                            )}
                            <p className="mt-2 text-xs text-gray-500 dark:text-zinc-400">
                                Estimado atual: {fmtBrl(progress.estimated_cashback_brl)} sobre vendas Buckler One
                            </p>
                        </>
                    ) : (
                        <>
                            <p className="text-xl font-black text-gray-900 dark:text-white">
                                {fmtBrl(progress.estimated_cashback_brl)}
                                <span className="text-sm font-medium text-gray-500"> estimado no período</span>
                            </p>
                        </>
                    )}

                    {partnership.cashback_notes && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{partnership.cashback_notes}</p>
                    )}
                </div>

                {/* Condições */}
                {partnership.conditions_notes && (
                    <div className="sm:col-span-2 rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-zinc-800 dark:bg-zinc-800/50">
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Condições da parceria</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{partnership.conditions_notes}</p>
                    </div>
                )}
            </div>
        </article>
    );
}

function PartnershipForm({
    initial,
    franchisors,
    onSave,
    onCancel,
    loading,
}: {
    initial?: BucklerOnePartnership;
    franchisors: FranchisorOption[];
    onSave: (data: FormData) => void;
    onCancel: () => void;
    loading: boolean;
}) {
    const [form, setForm] = useState<FormData>({
        client_id: initial?.client_id ?? null,
        contract_started_at: initial?.contract_started_at ?? new Date().toISOString().slice(0, 10),
        target_total_brl: initial?.target_total_brl ?? 0,
        min_buckler_unit_pct: initial?.min_buckler_unit_pct ?? 30,
        cashback_rate: initial?.cashback_rate ?? 0,
        reference_machine_value_brl: initial?.reference_machine_value_brl ?? null,
        conditions_notes: initial?.conditions_notes ?? null,
        cashback_notes: initial?.cashback_notes ?? null,
        min_cashback_paid: initial?.min_cashback_paid ?? false,
        min_cashback_paid_amount_brl: initial?.min_cashback_paid_amount_brl ?? null,
        min_cashback_paid_at: initial?.min_cashback_paid_at ?? null,
        active: initial?.active ?? true,
    });
    const [franchisorSearch, setFranchisorSearch] = useState('');

    const { data: searchResults } = useQuery({
        queryKey: ['buckler-one-franchisors', franchisorSearch],
        queryFn: async () => {
            const params = franchisorSearch ? { search: franchisorSearch } : {};
            const { data } = await api.get('/commissioning/buckler-one/franchisors', { params });
            return (data.data ?? []) as FranchisorOption[];
        },
        staleTime: 30_000,
    });

    const options = searchResults ?? franchisors;

    return (
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-5 space-y-4 dark:border-orange-900/50 dark:bg-orange-950/30">
            <h3 className="font-bold text-gray-900 dark:text-white">
                {initial ? 'Editar parceria' : 'Nova parceria Buckler One'}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Franqueadora</label>
                    <input
                        type="search"
                        placeholder="Buscar franqueadora…"
                        className="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                        value={franchisorSearch}
                        onChange={(e) => setFranchisorSearch(e.target.value)}
                    />
                    <select
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                        value={form.client_id ?? ''}
                        onChange={(e) => setForm((p) => ({ ...p, client_id: e.target.value ? parseInt(e.target.value) : null }))}
                        disabled={!!initial}
                    >
                        <option value="">Selecionar franqueadora…</option>
                        {options.map((f) => (
                            <option key={f.id} value={f.id}>
                                {f.name} {f.units_count > 0 ? `(${f.units_count} un.)` : ''}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Início do contrato</label>
                    <input
                        type="date"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                        value={form.contract_started_at}
                        onChange={(e) => setForm((p) => ({ ...p, contract_started_at: e.target.value }))}
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Meta total (R$)</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono dark:border-zinc-600 dark:bg-zinc-900"
                        value={formatBrlInput(form.target_total_brl)}
                        onChange={(e) => setForm((p) => ({
                            ...p,
                            target_total_brl: parseBrlInput(e.target.value),
                        }))}
                        placeholder="R$ 0"
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Mín. unidades Buckler (%)</label>
                    <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                        value={form.min_buckler_unit_pct}
                        onChange={(e) => setForm((p) => ({ ...p, min_buckler_unit_pct: parseFloat(e.target.value) || 0 }))}
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Cashback (%)</label>
                    <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                        value={form.cashback_rate * 100}
                        onChange={(e) => setForm((p) => ({ ...p, cashback_rate: (parseFloat(e.target.value) || 0) / 100 }))}
                    />
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Ticket médio máquina (R$)</label>
                    <input
                        type="number"
                        min={0}
                        step={1000}
                        placeholder="Para calcular qtd. de máquinas"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                        value={form.reference_machine_value_brl ?? ''}
                        onChange={(e) => setForm((p) => ({
                            ...p,
                            reference_machine_value_brl: e.target.value ? parseFloat(e.target.value) : null,
                        }))}
                    />
                </div>

                <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Condições da parceria</label>
                    <textarea
                        rows={3}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                        value={form.conditions_notes ?? ''}
                        onChange={(e) => setForm((p) => ({ ...p, conditions_notes: e.target.value || null }))}
                        placeholder="Ex.: mínimo 30% das unidades novas com equipamentos Buckler…"
                    />
                </div>

                <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Regras de cashback</label>
                    <textarea
                        rows={2}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                        value={form.cashback_notes ?? ''}
                        onChange={(e) => setForm((p) => ({ ...p, cashback_notes: e.target.value || null }))}
                    />
                </div>

                <div className="sm:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 dark:border-emerald-900/40 dark:bg-emerald-950/20">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-zinc-200">
                        <input
                            type="checkbox"
                            checked={form.min_cashback_paid}
                            onChange={(e) => setForm((p) => ({
                                ...p,
                                min_cashback_paid: e.target.checked,
                                min_cashback_paid_amount_brl: e.target.checked
                                    ? (p.min_cashback_paid_amount_brl ?? 0)
                                    : null,
                                min_cashback_paid_at: e.target.checked
                                    ? (p.min_cashback_paid_at ?? new Date().toISOString().slice(0, 10))
                                    : null,
                            }))}
                            className="rounded"
                        />
                        Cashback mínimo já pago à franqueadora
                    </label>

                    {form.min_cashback_paid && (
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Valor pago (R$)</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono dark:border-zinc-600 dark:bg-zinc-900"
                                    value={formatBrlInput(form.min_cashback_paid_amount_brl ?? 0)}
                                    onChange={(e) => setForm((p) => ({
                                        ...p,
                                        min_cashback_paid_amount_brl: parseBrlInput(e.target.value) || null,
                                    }))}
                                    placeholder="R$ 0"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Data do pagamento</label>
                                <input
                                    type="date"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                                    value={form.min_cashback_paid_at ?? ''}
                                    onChange={(e) => setForm((p) => ({
                                        ...p,
                                        min_cashback_paid_at: e.target.value || null,
                                    }))}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center">
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={form.active}
                            onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                            className="rounded"
                        />
                        Parceria ativa
                    </label>
                </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={onCancel}>Cancelar</Button>
                <Button
                    onClick={() => onSave(form)}
                    isLoading={loading}
                    disabled={!form.client_id}
                >
                    Salvar
                </Button>
            </div>
        </div>
    );
}

export default function BucklerOnePage() {
    const queryClient = useQueryClient();
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<BucklerOnePartnership | null>(null);

    const { data: partnerships = [], isLoading } = useQuery({
        queryKey: ['buckler-one-partnerships'],
        queryFn: async () => {
            const { data } = await api.get('/commissioning/buckler-one');
            return (data.data ?? []) as BucklerOnePartnership[];
        },
    });

    const { data: franchisors = [] } = useQuery({
        queryKey: ['buckler-one-franchisors-initial'],
        queryFn: async () => {
            const { data } = await api.get('/commissioning/buckler-one/franchisors');
            return (data.data ?? []) as FranchisorOption[];
        },
        staleTime: 60_000,
    });

    const saveMutation = useMutation({
        mutationFn: async ({ id, payload }: { id?: number; payload: FormData }) => {
            if (id) {
                await api.put(`/commissioning/buckler-one/${id}`, payload);
            } else {
                await api.post('/commissioning/buckler-one', payload);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['buckler-one-partnerships'] });
            toast.success('Parceria salva');
            setFormOpen(false);
            setEditing(null);
        },
        onError: () => toast.error('Erro ao salvar parceria'),
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: number) => api.delete(`/commissioning/buckler-one/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['buckler-one-partnerships'] });
            toast.success('Parceria removida');
        },
        onError: () => toast.error('Erro ao remover'),
    });

    function handleDelete(p: BucklerOnePartnership) {
        if (!window.confirm(`Remover parceria com ${p.franchisor?.name}?`)) return;
        deleteMutation.mutate(p.id);
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Buckler One"
                breadcrumb={[{ name: 'Comissionamento' }, { name: 'Buckler One' }]}
                description="Parcerias com franqueadoras — metas, mix mínimo Buckler, cashback e progresso de vendas."
            />

            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <PiHandshakeBold className="w-5 h-5 text-orange-500" />
                    {partnerships.length} parceria(s) cadastrada(s)
                </div>
                {!formOpen && (
                    <Button
                        onClick={() => { setEditing(null); setFormOpen(true); }}
                        className="flex items-center gap-2"
                    >
                        <PiPlusBold className="w-4 h-4" />
                        Nova parceria
                    </Button>
                )}
            </div>

            {formOpen && (
                <PartnershipForm
                    initial={editing ?? undefined}
                    franchisors={franchisors}
                    onSave={(payload) => saveMutation.mutate({ id: editing?.id, payload })}
                    onCancel={() => { setFormOpen(false); setEditing(null); }}
                    isLoading={saveMutation.isPending}
                />
            )}

            {isLoading ? (
                <LoadingState variant="cards" />
            ) : partnerships.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 p-12 text-center dark:border-zinc-700">
                    <PiHandshakeBold className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                    <p className="font-medium text-gray-700 dark:text-gray-300">Nenhuma parceria Buckler One</p>
                    <p className="mt-1 text-sm text-gray-500">
                        Cadastre a franqueadora, data de início do contrato, meta e condições.
                    </p>
                </div>
            ) : (
                <div className="grid gap-6">
                    {partnerships.map((p) => (
                        <PartnershipCard
                            key={p.id}
                            partnership={p}
                            onEdit={() => { setEditing(p); setFormOpen(true); }}
                            onDelete={() => handleDelete(p)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
